import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppButton } from '@/shared/ui/button';
import { Icon } from '@/shared/ui/icons';
import { AppInput } from '@/shared/ui/input';
import {
  Menu,
  MenuLabel,
  MenuOption,
  MenuSeparator,
  useAnchor,
} from '@/shared/ui/menu';
import { Sheet } from '@/shared/ui/sheet';
import { AppText } from '@/shared/ui/text';
import { radii, spacing, useTheme } from '@/shared/ui/theme';
import type { Preset } from '../lib/view-store';

/**
 * «Виды» — сохранённые раскладки с камерами, как в iVMS: «Склад», «Входы», «Ночь».
 * Один щелчок — и раскладка с камерами в окнах на месте; карусель листает их по кругу.
 *
 * На кнопке написано имя открытого вида: по ней видно, что на экране сейчас — «Входы»,
 * а не просто какие-то камеры.
 */
const styles = StyleSheet.create({
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(1),
    height: 28,
    paddingHorizontal: spacing(2),
    borderRadius: radii.md,
    borderWidth: StyleSheet.hairlineWidth,
    maxWidth: 200,
  },
  label: { flexShrink: 1 },
  form: { gap: spacing(3) },
});

export function ViewsMenu({
  presets,
  activeId,
  onApply,
  onSave,
  onDelete,
}: {
  presets: Preset[];
  activeId: string | null;
  onApply: (preset: Preset) => void;
  onSave: (name: string) => void;
  onDelete: (preset: Preset) => void;
}) {
  const { t } = useTranslation();
  const p = useTheme();
  const { ref, anchor, measure } = useAnchor();
  const [open, setOpen] = useState(false);
  const [naming, setNaming] = useState(false);
  const active = presets.find(x => x.id === activeId) ?? null;
  const [name, setName] = useState('');

  const startSave = () => {
    setOpen(false);
    setName(active?.name ?? '');
    setNaming(true);
  };

  return (
    <>
      <View ref={ref} collapsable={false}>
        <Pressable
          testID="live-views"
          accessibilityRole="button"
          accessibilityLabel={t('live.view.views')}
          {...({ title: t('live.view.views') } as object)}
          onPress={() => measure(() => setOpen(true))}
          style={state => [
            styles.button,
            {
              borderColor: p.border,
              backgroundColor:
                state.pressed || (state as { hovered?: boolean }).hovered
                  ? p.accent
                  : 'transparent',
            },
          ]}
        >
          <Icon
            name="bookmark"
            size={16}
            color={active ? p.foreground : p.mutedForeground}
          />
          <AppText
            variant="caption"
            numberOfLines={1}
            color={active ? p.foreground : p.mutedForeground}
            style={styles.label}
          >
            {active ? active.name : t('live.view.views')}
          </AppText>
          <Icon name="chevronDown" size={12} color={p.mutedForeground} />
        </Pressable>
      </View>

      <Menu
        visible={open}
        onClose={() => setOpen(false)}
        anchor={anchor}
        title={t('live.view.views')}
        minWidth={240}
        testID="live-views-menu"
      >
        {presets.length > 0 ? (
          <>
            <MenuLabel>{t('live.view.views')}</MenuLabel>
            {presets.map(preset => (
              <MenuOption
                key={preset.id}
                label={preset.name}
                hint={t(`live.view.layout.${preset.layout}`)}
                selected={preset.id === activeId}
                onPress={() => {
                  onApply(preset);
                  setOpen(false);
                }}
                testID={`live-view-${preset.id}`}
              />
            ))}
            <MenuSeparator />
          </>
        ) : (
          <MenuLabel>{t('live.view.viewsEmpty')}</MenuLabel>
        )}
        <MenuOption
          icon="plus"
          label={
            active
              ? t('live.view.updateView', { name: active.name })
              : t('live.view.saveView')
          }
          selected={false}
          onPress={startSave}
          testID="live-view-save"
        />
        {active ? (
          <MenuOption
            icon="plus"
            label={t('live.view.saveAsNew')}
            selected={false}
            onPress={() => {
              setOpen(false);
              setName('');
              setNaming(true);
            }}
          />
        ) : null}
        {active ? (
          <MenuOption
            icon="trash"
            danger
            label={t('live.view.deleteView', { name: active.name })}
            selected={false}
            onPress={() => {
              onDelete(active);
              setOpen(false);
            }}
            testID="live-view-delete"
          />
        ) : null}
      </Menu>

      <Sheet
        visible={naming}
        onClose={() => setNaming(false)}
        title={t('live.view.saveView')}
        subtitle={t('live.view.saveViewHint')}
        testID="live-view-name"
      >
        <View style={styles.form}>
          <AppInput
            value={name}
            onChangeText={setName}
            placeholder={t('live.view.viewNamePlaceholder')}
            autoFocus
            onSubmitEditing={() => {
              if (name.trim()) {
                onSave(name);
                setNaming(false);
              }
            }}
            testID="live-view-name-input"
          />
          <AppButton
            title={t('actions.save')}
            disabled={!name.trim()}
            onPress={() => {
              onSave(name);
              setNaming(false);
            }}
            testID="live-view-name-save"
          />
        </View>
      </Sheet>
    </>
  );
}
